import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { orderApi, orderError } from '../../actions/api/order';
import { dateBoundary } from './orderView';

export const initialFilters = { q: '', office: 'all', status: '', payment: '', owner: '', visibility: 'visible', dateFrom: '', dateTo: '', sort: 'createdAtDesc', limit: 20, page: 1 };
export default function useOrdersPage(notificationId) {
  const [filters, setFilters] = useState(initialFilters);
  const [search, setSearch] = useState('');
  const [list, setList] = useState({ orders: [], total: 0, totals: {} });
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [offices, setOffices] = useState([]);
  const [selectedId, setSelectedId] = useState(notificationId || '');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [notice, setNotice] = useState('');
  const [reload, setReload] = useState(0);
  const [detailReload, setDetailReload] = useState(0);
  const detailRequest = useRef(0);
  const activeId = useRef(selectedId);
  activeId.current = selectedId;
  useEffect(() => { setSelectedId(notificationId || ''); }, [notificationId]);
  useEffect(() => {
    const timer = setTimeout(() => setFilters((current) => current.q === search.trim() ? current : { ...current, q: search.trim(), page: 1 }), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const params = useMemo(() => ({ ...filters, dateFrom: dateBoundary(filters.dateFrom), dateTo: dateBoundary(filters.dateTo, true) }), [filters]);
  useEffect(() => {
    let active = true;
    if (notificationId) return undefined;
    setListLoading(true); setListError('');
    orderApi.list(params).then((data) => {
      if (!active) return;
      const pages = Math.max(1, Math.ceil(data.total / filters.limit));
      if (filters.page > pages) { setFilters((current) => ({ ...current, page: pages })); return; }
      setList(data);
    }).catch((error) => { if (active) setListError(orderError(error)); })
      .finally(() => { if (active) setListLoading(false); });
    return () => { active = false; };
  }, [params, reload, notificationId, filters.page, filters.limit]);
  useEffect(() => {
    let active = true;
    orderApi.filters().then((data) => { if (active) setOffices(data.offices || []); }).catch(() => { /* Основной список остаётся доступным. */ });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    const request = ++detailRequest.current;
    setDetailError('');
    if (!selectedId) { setDetail(null); setDetailLoading(false); return undefined; }
    setDetail((current) => current?._id === selectedId ? current : null);
    setDetailLoading(true);
    orderApi.detail(selectedId).then((data) => { if (detailRequest.current === request) setDetail(data); })
      .catch((error) => { if (detailRequest.current === request) setDetailError(orderError(error)); })
      .finally(() => { if (detailRequest.current === request) setDetailLoading(false); });
    return () => { if (detailRequest.current === request) detailRequest.current += 1; };
  }, [selectedId, detailReload]);
  const changeFilter = useCallback((key, value) => { setFilters((current) => ({ ...current, [key]: value, page: key === 'page' ? value : 1 })); }, []);
  const resetFilters = useCallback(() => { setSearch(''); setFilters(initialFilters); }, []);
  const selectOrder = useCallback((id) => {
    if (busyRef.current) return;
    setSelectedId(id); setDetail(null); setDetailError(''); setDetailLoading(Boolean(id)); setNotice('');
  }, []);
  const lockPayment = useCallback((value) => { busyRef.current = value; setBusy(value); }, []);
  const refresh = useCallback(() => { setReload((current) => current + 1); setDetailReload((current) => current + 1); }, []);
  const refreshDetail = useCallback(async () => {
    const id = activeId.current;
    const request = ++detailRequest.current;
    const data = await orderApi.detail(id);
    if (activeId.current === id && detailRequest.current === request) { setDetail(data); setDetailLoading(false); }
    setReload((current) => current + 1);
    return data;
  }, []);
  const mutate = useCallback(async (command, payload = {}) => {
    if (busyRef.current) throw new Error('Дождитесь завершения сохранения');
    const id = activeId.current;
    if (!id || detail?._id !== id) throw new Error('Сначала загрузите карточку заказа');
    busyRef.current = true; setBusy(true); setDetailError(''); setNotice('');
    try {
      const data = command === 'deleteOrder' ? await orderApi.remove(id) : await orderApi.command(command, { ...payload, orderId: id, expectedRevision: detail.financeRevision || 0 });
      if (activeId.current === id) {
        if (command === 'deleteOrder') { setSelectedId(''); setDetail(null); }
        else if (data.orderDetails || data.order || data.result) setDetail(data.orderDetails || data.order || data.result);
        setNotice(data.message || 'Изменения сохранены');
      }
      setReload((current) => current + 1);
      return data;
    } catch (error) {
      if (activeId.current === id) setDetailError(orderError(error));
      // После конфликта сохраняем прежнюю версию: пользователь закрывает форму
      // и явно обновляет карточку перед повторным редактированием.
      throw error;
    } finally { busyRef.current = false; setBusy(false); }
  }, [detail]);
  return { filters, search, setSearch, list, listLoading, listError, offices, selectedId, detail, detailLoading, detailError, busy, notice, changeFilter, resetFilters, selectOrder, refresh, refreshDetail, mutate, lockPayment };
}
